import { useState } from "react";
import { Users, Sparkles } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { useSubscription } from "@/lib/subscriptionContext";
import TeamDrawer from "./TeamDrawer";

/**
 * Top-bar Team icon.
 * - Free (and legacy Starter): hidden entirely.
 * - Pro: visible, opens an inline upgrade nudge popover.
 * - Studio (business) / Enterprise: opens the Team drawer.
 */
export default function TeamButton() {
  const { plan } = useSubscription();
  const navigate = useNavigate();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [nudgeOpen, setNudgeOpen] = useState(false);

  // Legacy 'starter' is normalized to 'free' in subscriptionContext, but keep
  // the explicit check for safety.
  if (plan === "free" || plan === "starter") return null;

  const trigger = (
    <Button
      size="sm"
      variant="outline"
      className="h-8 w-8 p-0 sm:w-auto sm:px-3 sm:gap-1.5"
      aria-label="Team"
    >
      <Users className="w-4 h-4" />
      <span className="hidden sm:inline text-xs">Team</span>
    </Button>
  );

  if (plan === "pro") {
    return (
      <Popover open={nudgeOpen} onOpenChange={setNudgeOpen}>
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        <PopoverContent align="end" className="w-72 p-4">
          <div className="flex items-start gap-3">
            <div className="rounded-md bg-primary/10 p-2 shrink-0">
              <Sparkles className="w-4 h-4 text-primary" />
            </div>
            <div className="space-y-2">
              <p className="text-sm font-medium text-foreground">Team collaboration</p>
              <p className="text-xs text-muted-foreground leading-relaxed">
                Team collaboration is available on Studio and Enterprise plans.
              </p>
              <Button
                size="sm"
                className="w-full mt-1 h-8 text-xs"
                onClick={() => {
                  setNudgeOpen(false);
                  navigate("/pricing");
                }}
              >
                See Studio plans
              </Button>
            </div>
          </div>
        </PopoverContent>
      </Popover>
    );
  }

  // business / enterprise
  return (
    <>
      <button
        type="button"
        onClick={() => setDrawerOpen(true)}
        className="inline-flex"
        aria-label="Open team"
      >
        {trigger}
      </button>
      <TeamDrawer open={drawerOpen} onOpenChange={setDrawerOpen} />
    </>
  );
}
